import { useState } from "react";
import { DuelGame } from "./DuelGame";
import { Game } from "./Game";
import { KumonMenu } from "./KumonMenu";
import { KumonResults } from "./KumonResults";
import { generateKumonQuestionSet } from "../game/kumonQuestions";
import { saveDuelRecord } from "../game/storage";
import {
  DUEL_QUESTION_COUNT,
  SOLO_QUESTION_COUNT,
  winnerOf,
  type Question,
  type RoundResult,
} from "../game/types";
import type { KumonGameConfig } from "../game/kumonTypes";

type KumonScreen =
  | { kind: "menu" }
  | { kind: "game"; config: KumonGameConfig; questions: Question[] }
  | { kind: "duel-game"; config: KumonGameConfig; questions: Question[] }
  | { kind: "results"; config: KumonGameConfig; p1: RoundResult; p2?: RoundResult };

interface KumonAppProps {
  onNavigateToClassic: () => void;
}

export function KumonApp({ onNavigateToClassic }: KumonAppProps) {
  const [screen, setScreen] = useState<KumonScreen>({ kind: "menu" });

  const start = (config: KumonGameConfig) => {
    if (config.mode === "duel") {
      setScreen({
        kind: "duel-game",
        config,
        questions: generateKumonQuestionSet(config.selectedCategory, DUEL_QUESTION_COUNT),
      });
    } else {
      setScreen({
        kind: "game",
        config,
        questions: generateKumonQuestionSet(config.selectedCategory, SOLO_QUESTION_COUNT),
      });
    }
  };

  const handleSoloFinish = (result: RoundResult) => {
    if (screen.kind !== "game") return;
    setScreen({ kind: "results", config: screen.config, p1: result });
  };

  const handleDuelFinish = (p1: RoundResult, p2: RoundResult) => {
    if (screen.kind !== "duel-game") return;
    saveDuelRecord({
      winner: winnerOf(p1, p2),
      p1,
      p2,
      date: new Date().toISOString(),
    }).catch(() => {});
    setScreen({ kind: "results", config: screen.config, p1, p2 });
  };

  const rematch = () => {
    if (screen.kind !== "results") return;
    start(screen.config);
  };

  const isGame = screen.kind === "game" || screen.kind === "duel-game";
  const isDuel = screen.kind === "duel-game";

  return (
    <div className={`app ${isGame ? "app-game" : ""} ${isDuel ? "app-duel" : ""}`}>
      {screen.kind === "menu" && (
        <KumonMenu onStart={start} onNavigateToClassic={onNavigateToClassic} />
      )}

      {screen.kind === "game" && (
        <Game
          key={screen.config.player1Name}
          name={screen.config.player1Name}
          questions={screen.questions}
          onFinish={handleSoloFinish}
        />
      )}

      {screen.kind === "duel-game" && (
        <DuelGame
          key={screen.config.player1Name}
          player1Name={screen.config.player1Name}
          player2Name={screen.config.player2Name}
          questions={screen.questions}
          onFinish={handleDuelFinish}
        />
      )}

      {screen.kind === "results" && (
        <KumonResults
          config={screen.config}
          p1={screen.p1}
          p2={screen.p2}
          onRematch={rematch}
          onMenu={() => setScreen({ kind: "menu" })}
        />
      )}
    </div>
  );
}
